import type {OmitStrict} from 'type-zoo'
import type {
  TMajorActionDealBreakerData,
  TPlayerId,
  TPortfolioNeighborhoodId,
} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import {DealBreakerCard} from '~/models/game/Cards/ActionCard'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import {unambiguousNeighborhoodDescriptionData} from '~/models/game/MajorActions/utils'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<TMajorActionDealBreakerData, 'card' | 'type'>

export default class MajorActionDealBreaker
  extends MajorAction<TMajorActionDealBreakerData>
  implements PublicMembersFromApi
{
  card: DealBreakerCard
  minorActorPlayerId: TPlayerId // the attacked player
  portfolioNeighborhoodId: TPortfolioNeighborhoodId // the to-be-deal-broken neighborhood

  constructor(data: TMajorActionDealBreakerData) {
    super(data)
    this.card = new DealBreakerCard(data.card)
    this.minorActorPlayerId = data.minorActorPlayerId
    this.portfolioNeighborhoodId = data.portfolioNeighborhoodId
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const minorActor = gamePosition.players.find(
      o => o.id === this.minorActorPlayerId
    )
    if (!minorActor) {
      throw new Error(`Minor actor not found in MajorActionSlyDeal description`)
    }

    const requestedPortfolioNeighborhood = minorActor.portfolio.neighborhoods.find(
      pn => pn.id === this.portfolioNeighborhoodId
    )

    if (!requestedPortfolioNeighborhood) {
      throw new Error(
        `Deal Breaker's requested PortfolioNeighborhood not found in minor actor's portfolio`
      )
    }

    const {sameColorCount, sameColorIndex} = unambiguousNeighborhoodDescriptionData(
      minorActor.portfolio,
      requestedPortfolioNeighborhood
    )
    const dropFirstNoun = tense === 'imperativePrompt'

    const {conjugatedVerb, xNoun, yNounPossessive} = xVerbY(
      tense,
      majorActor,
      dropFirstNoun ? 'Play' : 'play',
      minorActor,
      currentPlayer?.id
    )

    return skins[skin].gameLogDescriptions.majorActions.dealBreaker({
      tense,
      actor: dropFirstNoun
        ? undefined
        : xNoun === majorActor.username
          ? xNoun
          : sentenceCase(xNoun),
      playVerb: conjugatedVerb,
      cardName: this.card.displayName(skin),
      targetPossessive: yNounPossessive,
      neighborhoodColor: requestedPortfolioNeighborhood.displayColor(skin),
      neighborhoodIndex: sameColorCount > 1 ? `#${sameColorIndex + 1}` : undefined,
    })
  }
}
